import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import type { ProjectStatusFilter } from "@/features/projects/lib/projectListParams";
import { taskKeys } from "@/features/tasks/api";
import { api, unwrap, type Schemas } from "@/shared/api/client";
import { unwrapPage, type PageOf } from "@/shared/api/page";
import { makeKeys } from "@/shared/api/queryKeys";

export type ProjectSummary = Schemas["ProjectSummaryOut"];
/** A project as the detail endpoint returns it: the summary, with its counts and health. */
export type Project = ProjectSummary;
export type ProjectCreate = Schemas["ProjectCreate"];
export type ProjectUpdate = Schemas["ProjectUpdate"];
export type ServiceLine = Schemas["ServiceLine"];
export type ProjectMember = Schemas["ProjectMemberOut"];
export type ProjectRole = Schemas["ProjectRole"];
/** An active team member as pickers offer them (GET /team/people). */
export type StaffMember = Schemas["ColleagueOut"];
export type ClientSummary = Schemas["ClientSummaryOut"];

export const SERVICE_LINES: readonly ServiceLine[] = ["web", "mobile", "automation", "marketing"];

/* Lists are read whole: an agency has tens of projects, so filtering happens in place. */
const PROJECT_PAGE_SIZE = 100;
const MAX_PROJECT_PAGES = 10;
const PICKER_PAGE_SIZE = 100;

/** Lists hold summaries; a detail is one project by its key, with its members under it. */
export const projectKeys = makeKeys("projects");
/** People and clients offered by the work area's pickers (not the Team or Clients screens). */
const pickerKeys = makeKeys("work-pickers");

export function membersKey(projectKey: string) {
  return [...projectKeys.detail(projectKey), "members"] as const;
}

async function fetchAllProjects(status: ProjectStatusFilter): Promise<ProjectSummary[]> {
  const projects: ProjectSummary[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < MAX_PROJECT_PAGES; page += 1) {
    const result: PageOf<ProjectSummary> = await unwrapPage(
      api.GET("/api/v1/projects", {
        params: { query: { status, cursor, limit: PROJECT_PAGE_SIZE } },
      }),
    );
    projects.push(...result.items);
    cursor = result.nextCursor;
    if (cursor === null) break;
  }
  return projects;
}

/** The projects the person can see, by status tab ("open" is active and on hold). */
export function useProjects(status: ProjectStatusFilter = "open") {
  return useQuery({
    queryKey: projectKeys.list({ status }),
    queryFn: () => fetchAllProjects(status),
  });
}

/** A project from any cached list, shown while its own request is on its way. */
function cachedSummary(queryClient: QueryClient, key: string): ProjectSummary | undefined {
  for (const [, data] of queryClient.getQueriesData<ProjectSummary[]>({
    queryKey: projectKeys.lists(),
  })) {
    const found = data?.find((project) => project.key === key);
    if (found) return found;
  }
  return undefined;
}

export function useProject(key: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: projectKeys.detail(key),
    queryFn: () => unwrap(api.GET("/api/v1/projects/{key}", { params: { path: { key } } })),
    placeholderData: () => cachedSummary(queryClient, key),
    enabled: key !== "",
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectCreate) => unwrap(api.POST("/api/v1/projects", { body })),
    onSuccess: (project) => {
      queryClient.setQueryData(projectKeys.detail(project.key), project);
      return queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

export interface UpdateProjectVariables {
  changes: ProjectUpdate;
  /** What to show at once; the server's answer replaces it. */
  optimistic?: Partial<Project>;
}

/**
 * Edit a project's details or status. Edits go out one at a time, each with the version the
 * previous one returned (If-Match), and show at once; a refusal puts the old values back.
 */
export function useUpdateProject(key: string) {
  const queryClient = useQueryClient();
  const detailKey = projectKeys.detail(key);
  return useMutation({
    scope: { id: `project-edits:${key}` },
    mutationFn: ({ changes }: UpdateProjectVariables) => {
      const version = queryClient.getQueryData<Project>(detailKey)?.version;
      return unwrap(
        api.PATCH("/api/v1/projects/{key}", {
          params: {
            path: { key },
            header: version === undefined ? {} : { "if-match": String(version) },
          },
          body: changes,
        }),
      );
    },
    onMutate: async ({ optimistic }) => {
      await queryClient.cancelQueries({ queryKey: detailKey, exact: true });
      const previous = queryClient.getQueryData<Project>(detailKey);
      if (previous && optimistic)
        queryClient.setQueryData(detailKey, { ...previous, ...optimistic });
      return previous;
    },
    onSuccess: (project) => {
      queryClient.setQueryData(detailKey, project);
    },
    onError: (_error, _variables, previous) => {
      if (previous) queryClient.setQueryData(detailKey, previous);
      // Refused edits are often stale ones: fetch what the server has now.
      void queryClient.invalidateQueries({ queryKey: detailKey, exact: true });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: projectKeys.lists() }),
  });
}

/* ---- Members ---- */

export function useProjectMembers(key: string, enabled = true) {
  return useQuery({
    queryKey: membersKey(key),
    queryFn: () => unwrap(api.GET("/api/v1/projects/{key}/members", { params: { path: { key } } })),
    enabled: enabled && key !== "",
  });
}

/** After a membership change: the project's counts, and the lists that show members. */
function refreshAfterMembership(queryClient: QueryClient, key: string) {
  void queryClient.invalidateQueries({ queryKey: projectKeys.detail(key) });
  void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
}

export function useAddProjectMember(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["ProjectMemberAdd"]) =>
      unwrap(api.POST("/api/v1/projects/{key}/members", { params: { path: { key } }, body })),
    onSuccess: (members) => {
      queryClient.setQueryData(membersKey(key), members);
      refreshAfterMembership(queryClient, key);
    },
  });
}

/** The member list after `userId` takes `role`; a new lead demotes the old one, as on the server. */
function withRole(members: ProjectMember[], userId: string, role: ProjectRole): ProjectMember[] {
  return members.map((member) => {
    if (member.user.id === userId) return { ...member, role };
    if (role === "lead" && member.role === "lead") return { ...member, role: "contributor" };
    return member;
  });
}

export function useChangeMemberRole(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectRole }) =>
      unwrap(
        api.PATCH("/api/v1/projects/{key}/members/{user_id}", {
          params: { path: { key, user_id: userId } },
          body: { role },
        }),
      ),
    onMutate: async ({ userId, role }) => {
      await queryClient.cancelQueries({ queryKey: membersKey(key) });
      const previous = queryClient.getQueryData<ProjectMember[]>(membersKey(key));
      if (previous) queryClient.setQueryData(membersKey(key), withRole(previous, userId, role));
      return previous;
    },
    onSuccess: (members) => {
      queryClient.setQueryData(membersKey(key), members);
    },
    onError: (_error, _variables, previous) => {
      if (previous) queryClient.setQueryData(membersKey(key), previous);
    },
    onSettled: () => {
      refreshAfterMembership(queryClient, key);
    },
  });
}

export function useRemoveProjectMember(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      unwrap(
        api.DELETE("/api/v1/projects/{key}/members/{user_id}", {
          params: { path: { key, user_id: userId } },
        }),
      ),
    onMutate: async (userId) => {
      await queryClient.cancelQueries({ queryKey: membersKey(key) });
      const previous = queryClient.getQueryData<ProjectMember[]>(membersKey(key));
      if (previous) {
        queryClient.setQueryData(
          membersKey(key),
          previous.filter((member) => member.user.id !== userId),
        );
      }
      return previous;
    },
    onError: (_error, _userId, previous) => {
      if (previous) queryClient.setQueryData(membersKey(key), previous);
    },
    onSettled: () => {
      refreshAfterMembership(queryClient, key);
      // They were taken off their open tasks.
      void queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/* ---- Pickers ---- */

/** Active staff, for choosing a lead or a new member. Contractors cannot list the team. */
export function useActiveStaff(enabled = true) {
  return useQuery({
    queryKey: pickerKeys.list({ kind: "staff" }),
    // The whole team's names for pickers; open to everyone on the team (the team screen
    // itself stays behind team.view).
    queryFn: () => unwrap(api.GET("/api/v1/team/people")),
    enabled,
  });
}

/** Active clients, for linking a project to one. Contractors cannot list clients. */
export function useActiveClients(enabled = true) {
  return useQuery({
    queryKey: pickerKeys.list({ kind: "clients" }),
    queryFn: async () =>
      (
        await unwrapPage(
          api.GET("/api/v1/clients", {
            params: { query: { status: "active", limit: PICKER_PAGE_SIZE } },
          }),
        )
      ).items,
    enabled,
  });
}

/**
 * Suggest a key from a Latin project name: initials for several words ("Noon storefront
 * redesign" gives "NSR"), the word itself for one ("Talabat" gives "TALABAT"), at most 10
 * characters. Arabic names give "" and the person types the key.
 */
export function suggestProjectKey(name: string): string {
  const words = name.toUpperCase().match(/[A-Z0-9]+/g) ?? [];
  const firstWord = words[0] ?? "";
  if (!/^[A-Z]/.test(firstWord)) return "";
  const key = words.length > 1 ? words.map((word) => word[0]).join("") : firstWord;
  return (key.length >= 2 ? key : firstWord).slice(0, 10);
}
